import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FUNZIONI_IMPIANTO, TIMEOUT_FORZATURA_SEC, TIPI_CARICO, type Canale, type FunzioneImpianto, type TipoSonda } from '@emacomfort/domain';
import { useDevice, useStato } from '@/app/DeviceProvider';
import { useFormato } from '@/app/formato';
import { useAvvisi } from '@/app/useAvvisi';
import { Avviso, Badge, Bottone, Campo, Card, Conferma, Pagina, Select, Stepper, stili } from '@/ui';
import { useTecnico } from '../comune';
import s from '../features.module.css';

const TIPI_SONDA: TipoSonda[] = ['NTC_10K', 'PT1000', 'ANALOGICO', 'DIGITALE'];

function codificaAssegnazione(a: Canale['assegnazione']): string {
  if (a.tipo === 'zona') return `zona:${a.zonaId}`;
  if (a.tipo === 'funzione') return `funzione:${a.funzione}${a.generatoreId ? `:${a.generatoreId}` : ''}`;
  return 'nessuna';
}

function decodificaAssegnazione(v: string): Canale['assegnazione'] {
  if (v.startsWith('zona:')) return { tipo: 'zona', zonaId: v.slice(5) };
  if (v.startsWith('funzione:')) {
    const [, funzione, generatoreId] = v.split(':');
    return { tipo: 'funzione', funzione: funzione as FunzioneImpianto, ...(generatoreId ? { generatoreId } : {}) };
  }
  return { tipo: 'nessuna' };
}

function RigaIngresso({ canale }: { canale: Canale }) {
  const { t } = useTranslation();
  const f = useFormato();
  const { esegui } = useTecnico();
  const { client, store } = useDevice();
  const mostra = useAvvisi((a) => a.mostra);
  const impianto = useStato((st) => st.impianto);
  const [assegnazione, setAssegnazione] = useState(codificaAssegnazione(canale.assegnazione));
  const [tipoSonda, setTipoSonda] = useState<TipoSonda>(canale.tipoSonda ?? 'NTC_10K');
  const [offset, setOffset] = useState(canale.taratura.offset);
  const [fattore, setFattore] = useState(canale.taratura.fattore);
  useEffect(() => {
    setAssegnazione(codificaAssegnazione(canale.assegnazione));
    setTipoSonda(canale.tipoSonda ?? 'NTC_10K');
    setOffset(canale.taratura.offset);
    setFattore(canale.taratura.fattore);
  }, [canale]);
  const cambiato = assegnazione !== codificaAssegnazione(canale.assegnazione) || tipoSonda !== (canale.tipoSonda ?? 'NTC_10K') || offset !== canale.taratura.offset || fattore !== canale.taratura.fattore;
  if (!impianto) return null;

  const salva = async () => {
    await esegui((tec, sess) => tec.patchCanale(sess, canale.id, { assegnazione: decodificaAssegnazione(assegnazione), tipoSonda, taratura: { offset, fattore } }));
    mostra(t('tecnico.io.salvato', { canale: canale.id }), 'ok');
    // La mappa I/O è configurazione, non telemetria: si rilegge l'impianto.
    store.setState({ impianto: await client.getImpianto() });
  };

  return (
    <div className={s.rigaCanale}>
      <div>
        <div className={s.canaleId}>{canale.id}</div>
        {canale.confidenza && canale.confidenza !== 'alta' && <Badge tono="warn">{t(`tecnico.canali.confidenza.${canale.confidenza}`)}</Badge>}
        {canale.nota && <div className={s.letturaNota}>{t(`note.${canale.nota}`)}</div>}
      </div>
      <Campo etichetta={t('tecnico.io.assegnaA')}>
        <Select value={assegnazione} onChange={(e) => setAssegnazione(e.target.value)}>
          <option value="nessuna">{t('tecnico.io.nessuna')}</option>
          <optgroup label={t('tecnico.io.zona')}>
            {impianto.zone.map((z) => (
              <option key={z.id} value={`zona:${z.id}`}>
                {z.nome}
              </option>
            ))}
          </optgroup>
          <optgroup label={t('tecnico.io.funzione')}>
            {FUNZIONI_IMPIANTO.filter((fn) => !['VMC', 'LOGICA_GENERATORE', 'CIRCOLATORE', 'VALVOLA_DEVIATRICE', 'COMANDO_GENERATORE'].includes(fn)).flatMap((fn) =>
              fn === 'MANDATA_GENERATORE' || fn === 'RITORNO_GENERATORE'
                ? impianto.generatori.map((g) => (
                    <option key={`${fn}:${g.id}`} value={`funzione:${fn}:${g.id}`}>
                      {t(`tecnico.funzione.${fn}`)} · {g.nome}
                    </option>
                  ))
                : [
                    <option key={fn} value={`funzione:${fn}`}>
                      {t(`tecnico.funzione.${fn}`)}
                    </option>,
                  ],
            )}
          </optgroup>
        </Select>
      </Campo>
      <Campo etichetta={t('tecnico.io.tipoSonda')}>
        <Select value={tipoSonda} onChange={(e) => setTipoSonda(e.target.value as TipoSonda)}>
          {TIPI_SONDA.map((ts) => (
            <option key={ts} value={ts}>
              {t(`tecnico.io.sonda.${ts}`)}
            </option>
          ))}
        </Select>
      </Campo>
      <div className={stili.campo}>
        <span className={stili.etichetta}>
          {t('tecnico.io.offset')} · {t('tecnico.io.fattore')} {f.numero(fattore, 2)}
        </span>
        <Stepper dimensione="terziario" valore={f.numero(offset, 1)} onMeno={() => setOffset(Math.round((offset - 0.1) * 10) / 10)} onPiu={() => setOffset(Math.round((offset + 0.1) * 10) / 10)} etichettaMeno={`${t('tecnico.io.offset')} −0,1`} etichettaPiu={`${t('tecnico.io.offset')} +0,1`} />
      </div>
      <Bottone variante="primario" disabled={!cambiato} onClick={() => void salva()}>
        {t('tecnico.io.salva')}
      </Bottone>
    </div>
  );
}

function TestUscita({ canale }: { canale: Canale }) {
  const { t } = useTranslation();
  const f = useFormato();
  const { esegui } = useTecnico();
  const attuatore = useStato((st) => st.attuatori[canale.id]);
  const impianto = useStato((st) => st.impianto);
  const [stato, setStato] = useState<'attivo' | 'spento'>('attivo');
  const [timeout, setTimeoutSec] = useState(300);
  const [conferma, setConferma] = useState(false);
  const zone = impianto?.zone ?? [];
  const etichetta = canale.assegnazione.tipo === 'zona' ? zone.find((z) => z.id === (canale.assegnazione as { zonaId: string }).zonaId)?.nome : canale.assegnazione.tipo === 'funzione' ? t(`tecnico.funzione.${canale.assegnazione.funzione}`) : t('tecnico.canali.nonAssegnato');
  return (
    <div className={s.rigaCanale}>
      <div>
        <div className={s.canaleId}>{canale.id}</div>
        <div className={s.letturaNota}>{etichetta}</div>
        <Badge tono={attuatore?.stato === 'attivo' ? 'ok' : 'neutro'}>{attuatore?.stato === 'attivo' ? t('comune.attivo') : t('comune.spento')}</Badge>
      </div>
      {attuatore?.forzatoFinoA ? (
        <>
          <Avviso tono="warn">{t('tecnico.io.forzaturaAttiva', { ora: f.ora(attuatore.forzatoFinoA) })}</Avviso>
          <span />
          <span />
          <Bottone variante="pericolo" onClick={() => void esegui((tec, sess) => tec.annullaForzatura(sess, canale.id))}>
            {t('tecnico.io.annullaForzatura')}
          </Bottone>
        </>
      ) : (
        <>
          <Campo etichetta={t('tecnico.io.testUscita')}>
            <Select value={stato} onChange={(e) => setStato(e.target.value as 'attivo' | 'spento')}>
              <option value="attivo">{t('tecnico.io.forzaAttivo')}</option>
              <option value="spento">{t('tecnico.io.forzaSpento')}</option>
            </Select>
          </Campo>
          <Campo etichetta={t('tecnico.io.timeout')}>
            <Select value={timeout} onChange={(e) => setTimeoutSec(Number(e.target.value))}>
              {TIMEOUT_FORZATURA_SEC.map((sec) => (
                <option key={sec} value={sec}>
                  {t('tecnico.io.secondi', { n: sec })}
                </option>
              ))}
            </Select>
          </Campo>
          <span />
          <Bottone onClick={() => setConferma(true)}>{t('tecnico.io.avviaForzatura')}</Bottone>
        </>
      )}
      <Conferma
        aperta={conferma}
        titolo={t('tecnico.io.confermaForzatura', { canale: canale.id, stato: stato === 'attivo' ? t('comune.attivo') : t('comune.spento'), secondi: timeout })}
        onAnnulla={() => setConferma(false)}
        onConferma={() => {
          setConferma(false);
          void esegui((tec, sess) => tec.forzatura(sess, { canale: canale.id, stato, timeoutSec: timeout }));
        }}
      >
        {t('tecnico.io.timeoutNota')}
      </Conferma>
    </div>
  );
}

export function ConfigurazioneIoPage() {
  const { t } = useTranslation();
  const { esegui } = useTecnico();
  const { store, client } = useDevice();
  const impianto = useStato((st) => st.impianto);
  const mostra = useAvvisi((a) => a.mostra);

  if (!impianto) return null;
  const ingressi = impianto.canali.filter((c) => c.direzione === 'INPUT');
  const uscite = impianto.canali.filter((c) => c.direzione === 'OUTPUT');

  return (
    <Pagina titolo={t('tecnico.io.titolo')} sottotitolo={t('tecnico.io.sottotitolo')}>
      <Card titolo={t('tecnico.canali.ingressi')}>
        {ingressi.map((c) => (
          <RigaIngresso key={c.id} canale={c} />
        ))}
      </Card>
      <Card titolo={t('tecnico.io.tipoCarico')}>
        <p className={s.letturaNota}>{t('tecnico.io.tipoCaricoNota')}</p>
        <div className={s.campiGriglia}>
          {impianto.zone.map((z) => (
            <Campo key={z.id} etichetta={z.nome}>
              <Select
                value={z.tipoCarico}
                onChange={(e) => {
                  const tipo = e.target.value as (typeof TIPI_CARICO)[number];
                  void esegui((tec, sess) => tec.patchZonaCarico(sess, z.id, tipo)).then(async () => {
                    store.setState({ impianto: await client.getImpianto() });
                    mostra(`${z.nome}: ${t(`zone.carico.${tipo}`)}`, 'ok');
                  });
                }}
              >
                {TIPI_CARICO.map((tc) => (
                  <option key={tc} value={tc}>
                    {t(`zone.carico.${tc}`)}
                  </option>
                ))}
              </Select>
            </Campo>
          ))}
        </div>
      </Card>
      <Card titolo={t('tecnico.io.testUscita')}>
        <Avviso tono="info">{t('tecnico.io.timeoutNota')}</Avviso>
        {uscite.map((c) => (
          <TestUscita key={c.id} canale={c} />
        ))}
      </Card>
    </Pagina>
  );
}
